// Columna 2 de la ventana «Santi» para un contrato o para el chat con Santi (propuesta §5.1, §5.5; P1, P4).
// Cabecera: el título y, en la línea 2, lo que falta y de dónde viene («Te toca: subir la ruta y avisar a Santi · Te lo
// pidió Santi el 31 de octubre · Para el jueves 5»); a la derecha, siempre los mismos y en este orden: «Hecho»,
// «Cancelar», ⋯ («Contratar un agente para esto» / «Ver el agente que lo lleva»), y lo de la ventana. Ningún botón cambia
// según quién lo pidió. El chat con Santi es la misma pantalla sin «Hecho» ni «Cancelar» ni pista de turno.
// Conversación: solo personas, con separadores de día y la raya «Nuevo» encima de lo primero que Santi escribió desde la
// última vez que Pau lo abrió (se queda mientras esté abierto; al volver a abrirlo, ya no). Al tenerlo en pantalla CON Pau
// mirando (pestaña visible, con el foco y sin estar parado: usePauMira), su sesión marca lo visto (el punto se apaga);
// una pestaña de fondo nunca marca nada, y al volver se marca lo que hay en pantalla. Un contrato cerrado («Hecho esta semana»): sin «Hecho» ni «Cancelar»
// y, en lugar de la caja, «Hecho el 3 de noviembre.» con «Pedir algo a Santi».
// Socio (02-11): ve todo igual; en lugar de la caja, «Lo que le llega a Santi lo envía Pau», y sin «Hecho», «Cancelar»
// ni «Pedir algo» (no se pintan; nunca desactivados).
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Ban, Bot, Check, Loader2, MoreHorizontal, Plus, UserPlus } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { formatDiaMes } from '../../../lib/time';
import { cn } from '../../../lib/utils';
import { mensajeError } from '../errores';
import { useOficinaStore } from '../store';
import { BotonIcono } from '../ui';
import { pelotasApi } from './api';
import { CompositorSanti } from './CompositorSanti';
import { piezasDelHilo, primerNuevo, ultimoSeq, type Pieza } from './hilo';
import { CabeceraSanti, MarcoColumna } from './MarcoSanti';
import { usePauMira } from './mirando';
import { Globo, LoEnviaPau, RayaNuevo, SeparadorDia } from './piezas';
import { DURACION_LINEA_MS, useHablaComoPau, usePelotasStore } from './store';
import { etiquetaDia, hora, lineaCabecera, lineaCabeceraTexto, YO } from './tiempo';
import type { CerradoDTO, ContratoDTO, HiloDTO, HiloGeneralDTO, ListaContratosDTO, MensajeHiloDTO, MotivoCierre } from './tipos';

const ITEM_MENU = 'flex min-h-11 cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-[15px] text-oficina-texto outline-none data-[highlighted]:bg-white/[0.06]';

export interface PropsCharla {
  hilo: number | 'general';
  datos: HiloDTO | undefined;
  general: HiloGeneralDTO | null;
  lista: ListaContratosDTO | null;
  /** Lo que «Usar en el chat» dejó para la caja de este hilo. */
  relleno: { n: number; texto: string } | null;
  alRellenado: () => void;
  ancha: boolean;
  cajon: ReactNode;
  cajonAbierto: boolean;
  cajonEncima: boolean;
  linea: ReactNode;
  /** Cuántas líneas flotantes hay a la vista (0, 1 o 2): la conversación deja ese hueco abajo para no quedar tapada. */
  lineas: number;
  alCajon: () => void;
  alCerrar: () => void;
  alVerLista?: () => void;
  alMarcar: (c: ContratoDTO, motivo: MotivoCierre) => void;
  alPedirAlgo: () => void;
  alAbrirAgente: (agentId: string) => void;
}

export function CharlaSanti(p: PropsCharla) {
  const { hilo, datos, general, lista } = p;
  const esChat = hilo === 'general';
  const comoPau = useHablaComoPau();
  const enLista = esChat ? undefined : lista?.contratos.find((c) => c.temaId === hilo);
  const contrato: ContratoDTO | null = esChat ? null : (datos?.contrato ?? enLista ?? null);
  const cerradoSemana: CerradoDTO | undefined = esChat ? undefined : lista?.cerradosSemana.find((c) => c.temaId === hilo);
  const mensajes: MensajeHiloDTO[] | null = esChat ? (general?.mensajes ?? null) : (datos?.mensajes ?? null);
  const cierre = cierreDe(datos, cerradoSemana);
  const cerrado = !esChat && cierre !== null;

  // La raya «Nuevo»: dónde estaba lo nuevo AL ABRIR (la ventana monta esta columna de nuevo con cada hilo). Se fija una
  // vez, con la lista y la conversación a la vista, y antes de marcar lo visto (que pone `nuevos` a 0).
  const [nuevoDesde, setNuevoDesde] = useState<number | null | undefined>(undefined);
  const nuevos = esChat ? (lista?.general.nuevos ?? 0) : (enLista?.nuevos ?? 0);
  useEffect(() => {
    if (nuevoDesde !== undefined || !mensajes || !lista) return;
    setNuevoDesde(primerNuevo(mensajes, nuevos));
  }, [nuevoDesde, mensajes, lista, nuevos]);
  const marcarVisto = usePelotasStore((s) => s.marcarVisto);
  const mirando = usePauMira();
  useEffect(() => {
    if (!comoPau || !mirando || nuevoDesde === undefined || !mensajes) return;
    const hasta = ultimoSeq(mensajes);
    if (hasta > 0) marcarVisto(hilo, hasta);
  }, [comoPau, mirando, nuevoDesde, mensajes, hilo, marcarVisto]);

  const piezas = useMemo(() => (mensajes ? piezasDelHilo(mensajes, nuevoDesde ?? null) : []), [mensajes, nuevoDesde]);
  // Dónde se sujeta la conversación: abajo (lo último a la vista), en la raya «Nuevo» (al abrir, si lo nuevo no cabe) o
  // donde la dejó Pau (null: ha movido la rueda, el dedo o el teclado). Lo que crece después (un mensaje, la letra que
  // termina de cargar, la línea de «Hecho») la vuelve a poner en su ancla; si Pau ha subido a leer, no se le mueve.
  const cuerpo = useRef<HTMLDivElement>(null);
  const lista_ = useRef<HTMLOListElement>(null);
  const colocado = useRef(false);
  const ancla = useRef<'abajo' | 'raya' | null>('abajo');
  const colocar = () => {
    const el = cuerpo.current;
    if (!el || ancla.current === null) return;
    const raya = ancla.current === 'raya' ? el.querySelector<HTMLElement>('[data-testid="raya-nuevo"]') : null;
    el.scrollTop = raya ? Math.max(0, raya.offsetTop - 56) : el.scrollHeight;
  };
  useEffect(() => {
    if (!piezas.length) return;
    if (nuevoDesde !== undefined && !colocado.current) {
      // La primera vez que se sabe dónde va «Nuevo»: lo nuevo a la vista.
      colocado.current = true;
      ancla.current = nuevoDesde !== null ? 'raya' : 'abajo';
    } else if (colocado.current && ancla.current !== null) {
      // Llegó algo después: lo último.
      ancla.current = 'abajo';
    }
    colocar();
  }, [piezas, nuevoDesde]);
  const cargada = mensajes !== null;
  useEffect(() => {
    const el = cuerpo.current;
    const ol = lista_.current;
    if (!el || !ol) return;
    const suelta = () => {
      ancla.current = null;
    };
    const alMoverse = () => {
      if (ancla.current === null && el.scrollHeight - el.scrollTop - el.clientHeight < 40) ancla.current = 'abajo';
    };
    const intencion = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    for (const ev of intencion) el.addEventListener(ev, suelta, { passive: true });
    el.addEventListener('scroll', alMoverse, { passive: true });
    const vigia = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => colocar());
    vigia?.observe(ol);
    vigia?.observe(el);
    return () => {
      vigia?.disconnect();
      for (const ev of intencion) el.removeEventListener(ev, suelta);
      el.removeEventListener('scroll', alMoverse);
    };
  }, [cargada]);
  // Sale la línea de «Hecho» / «Cancelar»: lo último sube por encima de ella.
  useEffect(() => {
    if (p.lineas > 0) {
      ancla.current = 'abajo';
      colocar();
    }
  }, [p.lineas]);

  // ── Cabecera ──
  let titulo: string;
  let sub: ReactNode;
  let subCompleta: string | undefined;
  if (esChat) {
    titulo = t('oficinaV2.pelotas.lista.chat');
    sub = t('oficinaV2.pelotas.ventana.chatSub');
  } else {
    titulo = contrato?.titulo ?? cerradoSemana?.titulo ?? '';
    if (!contrato) sub = '';
    else if (cerrado) {
      const origen = contrato.pedidoEn ? lineaCabecera(contrato)[1]?.texto : undefined;
      sub = [t(cierre!.motivo === 'cancelado' ? 'oficinaV2.pelotas.ventana.cancelado' : 'oficinaV2.pelotas.ventana.hecho'), origen].filter(Boolean).join(' · ');
    } else {
      const partes = lineaCabecera(contrato);
      subCompleta = lineaCabeceraTexto(partes);
      const vista = p.ancha ? partes : partes.slice(0, 1);
      sub = vista.map((x, i) => (
        <span key={i} className={cn(x.tono === 'bloqueado' && 'font-semibold text-oficina-bloqueado', x.tono === 'error' && 'font-semibold text-oficina-error')}>
          {i ? <span className="mx-1.5 text-oficina-texto3">·</span> : null}
          {x.etiqueta ? <b className="font-semibold text-oficina-texto">{x.etiqueta} </b> : null}
          {x.texto}
        </span>
      ));
    }
  }

  const acciones = esChat ? null : (
    <>
      {contrato && !cerrado && comoPau ? (
        <>
          <button
            type="button"
            onClick={() => p.alMarcar(contrato, 'hecho')}
            className="inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-[10px] bg-white/[0.06] px-4 text-[16px] font-semibold text-oficina-texto outline-none ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/85"
            data-testid="boton-hecho"
          >
            <Check className="h-[18px] w-[18px]" aria-hidden />
            {t('oficinaV2.pelotas.ventana.hecho')}
          </button>
          <button
            type="button"
            onClick={() => p.alMarcar(contrato, 'cancelado')}
            className="inline-flex h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-[10px] px-3 text-[16px] font-semibold text-oficina-texto2 outline-none transition-colors hover:bg-white/[0.06] hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-white/85"
            data-testid="boton-cancelar"
          >
            <Ban className="h-[18px] w-[18px]" aria-hidden />
            {t('oficinaV2.pelotas.ventana.cancelar')}
          </button>
        </>
      ) : null}
      {contrato ? <MenuContrato temaId={contrato.temaId} agentId={contrato.agentId} cerrado={cerrado} alAbrirAgente={p.alAbrirAgente} /> : null}
    </>
  );

  // ── Pie ──
  let pie: ReactNode;
  if (cerrado) {
    pie = (
      <footer className="flex shrink-0 flex-wrap items-center justify-center gap-x-4 gap-y-2 border-t border-oficina-borde bg-oficina-panel px-6 py-3" data-testid="compositor-cerrado">
        <p className="text-center text-[15px] leading-5 text-oficina-texto2">
          {t(cierre!.motivo === 'cancelado' ? 'oficinaV2.pelotas.ventana.canceladoEl' : 'oficinaV2.pelotas.ventana.hechoEl', { fecha: formatDiaMes(cierre!.en) })}
        </p>
        {comoPau ? (
          <button
            type="button"
            onClick={p.alPedirAlgo}
            className="inline-flex h-11 items-center gap-2 rounded-[10px] bg-white/[0.06] px-4 text-[16px] font-semibold text-oficina-texto outline-none ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-white/85"
          >
            <Plus className="h-[18px] w-[18px]" aria-hidden />
            {t('oficinaV2.pelotas.lista.pedirAlgo')}
          </button>
        ) : null}
      </footer>
    );
  } else if (!comoPau) {
    pie = (
      <footer className="shrink-0 border-t border-oficina-borde bg-oficina-panel px-6 py-3" data-testid="compositor-solo-pau">
        <LoEnviaPau className="justify-center text-center" />
      </footer>
    );
  } else {
    pie = <CompositorSanti key={String(hilo)} hilo={hilo} conTurno={!!contrato && contrato.lado === YO} corta={p.cajonAbierto && !p.cajonEncima} relleno={p.relleno} alRellenado={p.alRellenado} />;
  }

  return (
    <MarcoColumna
      cabecera={<CabeceraSanti titulo={titulo} sub={sub} subCompleta={subCompleta} acciones={acciones} ancha={p.ancha} cajonAbierto={p.cajonAbierto} alCajon={p.alCajon} alCerrar={p.alCerrar} alVerLista={p.alVerLista} />}
      cuerpo={
        <div
          ref={cuerpo}
          className={cn(
            'absolute inset-0 overflow-y-auto overscroll-contain px-8 pt-4 [mask-image:linear-gradient(to_bottom,transparent,black_40px)] max-md:px-4',
            p.lineas === 0 ? 'pb-6' : p.lineas === 1 ? 'pb-24' : 'pb-40',
          )}
          data-testid="charla-cuerpo"
        >
          {mensajes === null ? (
            <p className="flex h-full items-center justify-center text-sm text-oficina-texto3">{t('oficinaV2.pelotas.ventana.cargando')}</p>
          ) : (
            <ol ref={lista_} className="flex min-h-full flex-col justify-end">
              {!piezas.length ? <li className="py-6 text-center text-[15px] text-oficina-texto3">{t('oficinaV2.pelotas.ventana.sinMensajes')}</li> : null}
              {piezas.map((x) => (
                <li key={x.clave}>
                  <PiezaHilo p={x} />
                </li>
              ))}
            </ol>
          )}
        </div>
      }
      pie={pie}
      linea={p.linea}
      cajon={p.cajon}
      cajonEncima={p.cajonEncima}
    />
  );
}

/** Cómo y cuándo se cerró (de la conversación; si aún no ha llegado, de «Hecho esta semana»). null = abierto. */
function cierreDe(datos: HiloDTO | undefined, semana: CerradoDTO | undefined): { motivo: MotivoCierre; en: string } | null {
  if (datos && datos.estado !== 'hecho') return null;
  const m = datos ? [...datos.mensajes].reverse().find((x) => x.tipo === 'cerrado') : undefined;
  if (semana) return { motivo: semana.motivo, en: semana.cerradoEn };
  if (datos) return { motivo: m?.motivo ?? 'hecho', en: m?.en ?? datos.contrato.desde };
  return null;
}

function PiezaHilo({ p }: { p: Pieza }) {
  switch (p.tipo) {
    case 'dia':
      return <SeparadorDia>{etiquetaDia(p.en)}</SeparadorDia>;
    case 'nuevo':
      return <RayaNuevo />;
    case 'globo':
      return (
        <Globo de={p.m.de === YO ? 'pau' : 'santi'} hora={hora(p.m.en)} ultimo={p.ultimo}>
          {p.m.texto}
        </Globo>
      );
  }
}

/**
 * ⋯ del contrato: «Contratar un agente para esto» (un agente normal, de la lista del equipo, que hace el trabajo técnico;
 * al contratarlo se abre su chat) o, si ya lo tiene, «Ver el agente que lo lleva».
 */
function MenuContrato({ temaId, agentId, cerrado, alAbrirAgente }: { temaId: number; agentId: string | null; cerrado: boolean; alAbrirAgente: (agentId: string) => void }) {
  const [contratando, setContratando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El motivo de un fallo se va solo, como la línea de «Hecho» (8 s).
  useEffect(() => {
    if (!error) return;
    const id = setTimeout(() => setError(null), DURACION_LINEA_MS);
    return () => clearTimeout(id);
  }, [error]);
  if (cerrado && !agentId) return null;
  const contratar = async () => {
    setContratando(true);
    setError(null);
    try {
      const r = await pelotasApi.agente(temaId);
      void usePelotasStore.getState().cargarLista();
      await useOficinaStore.getState().hidratar();
      alAbrirAgente(r.agentId);
    } catch (e) {
      setError(mensajeError(e));
    } finally {
      setContratando(false);
    }
  };
  return (
    <span className="relative inline-flex shrink-0">
      <Menu.Root>
        <Menu.Trigger asChild>
          <BotonIcono icono={contratando ? Loader2 : MoreHorizontal} etiqueta={t('oficinaV2.pelotas.ventana.masAcciones')} className={cn('h-11 w-11', contratando && '[&_svg]:animate-spin')} data-testid="menu-contrato" />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Content align="end" sideOffset={6} className="z-[70] min-w-[260px] rounded-xl bg-oficina-elevada p-1 shadow-2xl ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95">
            {agentId ? (
              <Menu.Item onSelect={() => alAbrirAgente(agentId)} className={ITEM_MENU} data-testid="menu-ver-agente">
                <Bot className="h-4 w-4 text-oficina-texto2" aria-hidden />
                {t('oficinaV2.pelotas.ventana.verAgente')}
              </Menu.Item>
            ) : (
              <Menu.Item onSelect={() => void contratar()} className={ITEM_MENU} data-testid="menu-contratar">
                <UserPlus className="h-4 w-4 text-oficina-texto2" aria-hidden />
                {t('oficinaV2.pelotas.ventana.contratarAgente')}
              </Menu.Item>
            )}
          </Menu.Content>
        </Menu.Portal>
      </Menu.Root>
      {error ? (
        <span role="alert" className="absolute right-0 top-full z-30 mt-2 w-max max-w-[320px] rounded-[10px] bg-oficina-elevada px-3 py-2 text-[14px] leading-5 text-oficina-error shadow-2xl ring-1 ring-white/10">
          {error}
        </span>
      ) : null}
    </span>
  );
}

